import { ArraySlice } from './arrayslice';

export const COMMENT_NODE_TAG = '#comment';
export const DOCUMENT_NODE_TAG = '#document';
/** Polyfill shadow nodes are not part of the visual DOM after web-capture Jan 17, 2025. */
export const POLYFILL_SHADOW_NODE_TAG = '#polyfillshadow';
export const SHADOW_NODE_TAG = '#shadow';
export const TEXT_NODE_TAG = '#text';
/**
 * Assigned nodes removed from web-capture Jan 17, 2025.
 * This is used for playback of old sessions only.
 * @deprecated
 */
export const ASSIGNED_NODE_TAG = '#assigned-nodes';

/**
 * A builder used by {@link decodeNodeTree} to construct a representation of a
 * tree of nodes. 'TreeNode' can be of any type, as long as this interface is
 * satisfied; it's not necessary to build an actual tree of objects.
 */
export interface NodeTreeBuilder<TreeNode> {
  createNode(tag: string): TreeNode;
  getParentNode(node: TreeNode): TreeNode | null;
  insertChildNode(parent: TreeNode, child: TreeNode, before: TreeNode | null): void;
  setNodeShadow(node: TreeNode, shadow: TreeNode): void;
  setNodeText(node: TreeNode, text: string): void;
  setNodeAttr(node: TreeNode, name: string, value: string): void;
}

/**
 * Reifies a JSON array (of the form produced by {@link tokenizeNode}) into an
 * abstract tree of DOM nodes. The concrete representation of the node tree is
 * determined by the provided {@link NodeTreeBuilder}.
 *
 * @param builder - The object that actually constructs the concrete node tree.
 * @param stream - A slice of a JSON array of the form produced by
 * {@link tokenizeNode}, representing the abstract tree of DOM nodes to decode.
 * @param parentNode - If non-null, the parent node under which the decoded
 * subtree will be attached. If null, the root of the decoded tree is treated as
 * the root of a document (if the root node is #document) or as a document
 * fragment.
 * @param beforeNode - If non-null, the existing child under the parent node
 * before which the decoded subtree should be inserted. If null, the decoded
 * subtree is appended to the child list of the parent node. Specifying a
 * non-null value for 'beforeNode' is an error if 'parentNode' is null, since
 * there's no child list that the decoded subtree will be inserted into.
 */
export function decodeNodeTree<TreeNode>(
  builder: NodeTreeBuilder<TreeNode>,
  stream: ArraySlice<string>,
  parentNode: TreeNode | null = null,
  beforeNode: TreeNode | null = null,
): void {
  if (parentNode === null && beforeNode !== null) {
    throw new Error(`Inserting decoded subtree before a node, but no parent was specified`);
  }

  const stack: (TreeNode | null)[] = [];
  if (parentNode) {
    // Put the parent (if present) at the top of the stack, so everything else gets added to it.
    stack.push(parentNode);
  }
  stack.push(null); // Leave a placeholder for the root node.

  // Walk the stream.
  let before: TreeNode | null = beforeNode;
  let node: TreeNode | null = null;
  let idx = 0;
  while (idx < stream.length) {
    const val = stream.get(idx++);
    switch (val[0]) {
      case '<': {
        const tag = val.slice(1);
        node = builder.createNode(tag);

        const tip = stack[stack.length - 1];
        if (tip == null) {
          // New child (slot reserved in the 'push' case).
          if (stack.length > 1) {
            // If there's a parent, insert the new node into it.
            const parent = stack[stack.length - 2];
            if (!parent) {
              throw new Error(`No parent node to insert into`);
            }
            if (tag == SHADOW_NODE_TAG || tag === POLYFILL_SHADOW_NODE_TAG) {
              builder.setNodeShadow(parent, node);
            } else {
              builder.insertChildNode(parent, node, before);
              before = null;
            }
          }
        } else {
          // Sibling of whatever was previously there.
          const parent = builder.getParentNode(tip);
          if (!parent) {
            throw new Error(`Sibling node without parent`);
          }
          builder.insertChildNode(parent, node, null);
        }

        // Update the top of the stack. It is the last "sibling" for a given depth in the tree.
        stack[stack.length - 1] = node;

        // Call setText *after* setting parent so setText can tell if it needs to rewrite CSS
        if (tag == TEXT_NODE_TAG) {
          builder.setNodeText(node, stream.get(idx++));
        } else if (tag === COMMENT_NODE_TAG) {
          builder.setNodeText(node, '');
        }
        break;
      }
      case ':': {
        const attr = val.slice(1);
        const value = stream.get(idx++);
        if (!node) {
          throw new Error(`No node to set an attribute on`);
        }
        builder.setNodeAttr(node, attr, value);
        break;
      }
      case '[': {
        stack.push(null);
        break;
      }
      case ']': {
        stack.pop();
        break;
      }
      default: {
        throw new Error(
          `Unexpected token ${val[0]} in encoded node stream at ${stream.start + idx - 1}.`,
        );
      }
    }
  }
}
